import React, { useState, useEffect, useRef } from 'react';
import { Modal } from '../index';
import {
  Overlay,
  Container,
  Header,
  Title,
  CloseButton,
  Subtitle,
  Body,
  Sidebar,
  SidebarList,
  SidebarItem,
  Content,
  CollapsibleIndex,
} from '../Terms/styled';
import CloseIcon from '@mui/icons-material/Close';

const IUBENDA_API_URL = 'https://www.iubenda.com/api/privacy-policy/17860076';

function slugify(text: string) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

interface ModalPrivacyPolicyProps {
  open: boolean;
  onClose: () => void;
}

export const ModalPrivacyPolicy: React.FC<ModalPrivacyPolicyProps> = ({
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [html, setHtml] = useState('');
  const [headings, setHeadings] = useState<{ id: string; text: string }[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(false);
    fetch(IUBENDA_API_URL)
      .then((r) => r.text())
      .then((raw) => {
        let content = '';
        try {
          const json = JSON.parse(raw);
          content = json.content || '';
        } catch (e) {
          setError(true);
          setLoading(false);
          return;
        }
        setHtml(content);
        // Parse headings
        const temp = document.createElement('div');
        temp.innerHTML = content;
        const h2s = Array.from(temp.querySelectorAll('h2'));
        const hs = h2s.map((h) => {
          let id = h.getAttribute('id');
          if (!id) {
            id = slugify(h.textContent || '');
            h.setAttribute('id', id);
          }
          return { id, text: h.textContent || '' };
        });
        setHeadings(hs);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open]);

  // Carica lo script Iubenda dopo ogni aggiornamento dell'HTML
  useEffect(() => {
    if (!html || error || loading) return;
    if (process.env.NODE_ENV !== 'production') return;
    // Evita di caricare più volte
    if (
      !document.querySelector(
        'script[src="https://cdn.iubenda.com/iubenda.js"]',
      )
    ) {
      const script = document.createElement('script');
      script.src = 'https://cdn.iubenda.com/iubenda.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        if (window && (window as any).iubenda) {
          (window as any).iubenda.process && (window as any).iubenda.process();
        }
      };
    } else {
      // Se già presente, triggera process manualmente
      if (window && (window as any).iubenda) {
        (window as any).iubenda.process && (window as any).iubenda.process();
      }
    }
  }, [html, error, loading]);

  // Scrollspy
  useEffect(() => {
    if (!open || !contentRef.current) return;
    const handler = () => {
      const sections = headings
        .map((h) => document.getElementById(h.id))
        .filter(Boolean);
      const scrollY = contentRef.current!.scrollTop;
      let current = headings[0]?.id || '';
      for (const s of sections) {
        if (s && s.offsetTop - 40 <= scrollY) current = s.id;
      }
      setActiveId(current);
    };
    const ref = contentRef.current;
    ref.addEventListener('scroll', handler);
    return () => ref.removeEventListener('scroll', handler);
  }, [headings, open]);

  const handleSidebarClick = (id: string) => {
    const el = document.getElementById(id);
    if (el && contentRef.current) {
      contentRef.current.scrollTo({
        top: el.offsetTop - 16,
        behavior: 'smooth',
      });
    }
  };

  return (
    <Overlay style={{ display: open ? 'flex' : 'none' }}>
      <Container>
        <Header>
          <Title>Privacy Policy</Title>
          <Subtitle>Last modified: July 16, 2025</Subtitle>
          <CloseButton aria-label="Close" onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </Header>
        <Body>
          <Sidebar>
            <SidebarList>
              {headings.map((h) => (
                <SidebarItem
                  key={h.id}
                  active={activeId === h.id}
                  onClick={() => handleSidebarClick(h.id)}
                >
                  {h.text}
                </SidebarItem>
              ))}
            </SidebarList>
          </Sidebar>
          <Content ref={contentRef} tabIndex={0}>
            {loading && <div>Loading…</div>}
            {error && (
              <div>
                Error loading.{' '}
                <a
                  href="https://www.iubenda.com/privacy-policy/17860076"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Privacy Policy
                </a>
              </div>
            )}
            {!loading && !error && (
              <div
                dangerouslySetInnerHTML={{ __html: html }}
                style={{ fontSize: 15, lineHeight: 1.5 }}
              />
            )}
          </Content>
        </Body>
      </Container>
    </Overlay>
  );
};
